import type { CreateSequencePayload, CreateSequenceStepPayload, Sequence, UpdateSequencePayload } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

async function uploadFiles(context: ApiContext, files: File[]): Promise<string[]> {
  if (files.length === 0) return []
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")
  return Promise.all(files.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
}

export interface StepFilesMap {
  [stepId: string]: {
    newFiles: File[]
    existingSignedIds: string[]
  }
}

export interface SequencesResponse {
  sequences: Sequence[]
}

export function fetchSequences(context: ApiContext, params?: { search?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.append("search", params.search)

  return apiContextFetch<SequencesResponse>(context, `sequences?${searchParams}`)
}

export async function fetchSequence(context: ApiContext, id: string) {
  return apiContextFetch<Sequence>(context, `sequences/${id}`)
}

export async function createSequence(context: ApiContext, payload: CreateSequencePayload) {
  return apiContextFetch<Sequence>(context, "sequences", {
    method: "POST",
    body: JSON.stringify({ sequence: payload }),
  })
}

export async function updateSequence(context: ApiContext, id: string, payload: UpdateSequencePayload) {
  return apiContextFetch<Sequence>(context, `sequences/${id}`, {
    method: "PUT",
    body: JSON.stringify({ sequence: payload }),
  })
}

export async function updateSequenceWithFiles(
  context: ApiContext,
  id: string,
  payload: UpdateSequencePayload,
  stepFiles: StepFilesMap
): Promise<Sequence> {
  // Upload files for each step and collect signed IDs
  const stepFileSignedIds: Record<string, string[]> = {}

  await Promise.all(
    Object.entries(stepFiles).map(async ([stepId, files]) => {
      const newSignedIds = await uploadFiles(context, files.newFiles)
      stepFileSignedIds[stepId] = [...files.existingSignedIds, ...newSignedIds]
    })
  )

  const updatedStepsAttributes = payload.sequence_steps_attributes?.map((stepAttr) => {
    const stepId = "id" in stepAttr ? stepAttr.id : "_tempId" in stepAttr ? (stepAttr._tempId as string) : undefined
    const signedIds = stepId ? stepFileSignedIds[stepId] : undefined

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _tempId, ...cleanStepAttr } = stepAttr as typeof stepAttr & { _tempId?: string }

    if (
      signedIds !== undefined &&
      "email_template_attributes" in cleanStepAttr &&
      cleanStepAttr.email_template_attributes
    ) {
      return {
        ...cleanStepAttr,
        email_template_attributes: {
          ...cleanStepAttr.email_template_attributes,
          files: signedIds,
        },
      }
    }

    return cleanStepAttr
  })

  const updatedPayload: UpdateSequencePayload = {
    ...payload,
    sequence_steps_attributes: updatedStepsAttributes as UpdateSequencePayload["sequence_steps_attributes"],
  }

  return apiContextFetch<Sequence>(context, `sequences/${id}`, {
    method: "PUT",
    body: JSON.stringify({ sequence: updatedPayload }),
  })
}

export async function deleteSequence(
  context: ApiContext,
  id: string,
  { onlyIfEmpty = false }: { onlyIfEmpty?: boolean } = {}
) {
  const path = onlyIfEmpty ? `sequences/${id}?only_if_empty=true` : `sequences/${id}`

  return apiContextFetch<void>(context, path, {
    method: "DELETE",
  })
}

export async function createSequenceStep(context: ApiContext, sequenceId: string, payload: CreateSequenceStepPayload) {
  return apiContextFetch<Sequence>(context, `sequences/${sequenceId}/sequence_steps`, {
    method: "POST",
    body: JSON.stringify({ sequence_step: payload }),
  })
}

export async function duplicateSequence(context: ApiContext, id: string, name: string) {
  return apiContextFetch<Sequence>(context, `sequences/${id}/duplicate`, {
    method: "POST",
    body: JSON.stringify({ sequence: { name } }),
  })
}
